import { FC, useCallback, useEffect, useState } from "react";
import { Trans } from "@lingui/macro";
import { connectCurrentRefinements } from "react-instantsearch-dom";
import {
  CurrentRefinementsProvided,
  Refinement,
  RefinementValue,
  SearchState,
} from "react-instantsearch-core";
import { find, isArray, last, values, without } from "lodash-es";
import { Flex } from "@otrium/core";
import {
  Button,
  StyledCloseButton,
  ColorBox,
  StyledText,
  Wrapper,
} from "./ActiveFilters.styled";
import {
  AGES_FACET,
  BRAND_FACET,
  CATEGORIES_FACET,
  COLOUR_FACET,
  CONSCIOUS_FACET,
  DISCOUNT_FACET,
  PATTERN_FACET,
  PRICE_FACET,
  PROMOTION_FACET,
  SIZE_FACET,
  STYLE_FACET,
} from "src/utils/algolia";
import { Price } from "src/atoms/Price";
import { filterFacets } from "src/organisms/AlgoliaProductList/utils/filterFacets";

const multicolorIconLink =
  "https://colors-icons.s3.eu-central-1.amazonaws.com/multicoloured.png";

interface CategoryItemProps {
  item: { label: string; value: RefinementValue };
  refine: () => void;
}
const RefineItem: FC<CategoryItemProps> = ({ item, refine, ...rest }) => {
  const splitItem = item.label.includes("||")
    ? item.label.split("||")
    : item.label.split(" > ");
  const label = item.label.includes("||") ? splitItem[1] : last(splitItem);
  return (
    <Flex variant="centered" {...rest}>
      <StyledText>{label}</StyledText>
      <StyledCloseButton onClick={refine} sx={{ width: 14, padding: 0 }} />
    </Flex>
  );
};

interface Props extends CurrentRefinementsProvided {
  onClose: () => void;
  onResetFilters: () => void;
  defaultPriceRange: { min: number; max: number };
  excludedFacetsList?: string[];
  keepSelectedIgnoreInActive?: { [p: string]: string[] };
  setStatePayload?: (
    payload: SearchState & { selectedCatSlug: string[] }
  ) => void;
}

const ActiveFilters: FC<Props> = ({
  onClose,
  onResetFilters,
  items,
  refine,
  defaultPriceRange,
  excludedFacetsList = [],
  keepSelectedIgnoreInActive,
  setStatePayload,
}) => {
  const brandFacet = !excludedFacetsList.includes(BRAND_FACET)
    ? find(items, { attribute: BRAND_FACET })
    : undefined;

  const [refined, setRefined] = useState(false);
  const activeFilters: { [key: string]: Refinement | undefined } = {
    [COLOUR_FACET]: find(items, { attribute: COLOUR_FACET }),
    [PRICE_FACET]: find(items, { attribute: PRICE_FACET }),
    [SIZE_FACET]: find(items, { attribute: SIZE_FACET }),
    [STYLE_FACET]: find(items, { attribute: STYLE_FACET }),
    [PATTERN_FACET]: find(items, { attribute: PATTERN_FACET }),
    [AGES_FACET]: find(items, { attribute: AGES_FACET }),
    [CATEGORIES_FACET]: filterFacets(
      find(items, { attribute: CATEGORIES_FACET }),
      keepSelectedIgnoreInActive
    ),
    [BRAND_FACET]: brandFacet,
    [PROMOTION_FACET]: find(
      items,
      (item) =>
        item.attribute === PROMOTION_FACET && !item.label.includes("ALL")
    ),
    [CONSCIOUS_FACET]: find(items, { attribute: CONSCIOUS_FACET }),
    [DISCOUNT_FACET]: find(items, { attribute: DISCOUNT_FACET }),
  };

  const priceFacet = activeFilters[PRICE_FACET];
  const priceRefinement = priceFacet?.currentRefinement as
    | { min: number; max: number }
    | undefined;
  const promotionFacet = activeFilters[PROMOTION_FACET];
  const discountFacet = activeFilters[DISCOUNT_FACET];
  let minPrice;
  let maxPrice;
  if (priceRefinement) {
    minPrice = Math.floor(priceRefinement.min || defaultPriceRange.min);
    maxPrice = Math.ceil(priceRefinement.max || defaultPriceRange.max);
  }

  const hasFilters = without(values(activeFilters), undefined).length > 0;

  const handleRefine = useCallback(
    (value: RefinementValue | Refinement[], callback: () => void = () => {}) =>
      () => {
        if (typeof value === "function") {
          refine(value);
        } else if (setStatePayload && isArray(value)) {
          const toRefine = value.reduce((a, b) => {
            a[b.attribute] = [];
            return a;
          }, {} as { [p: string]: string[] });

          setStatePayload({
            refinementList: toRefine,
            selectedCatSlug: [],
            range: undefined,
            menu: undefined,
          });
        }

        setRefined(true);
        callback();
      },
    [refine]
  );

  useEffect(() => {
    if (refined) {
      onClose();
      setRefined(false);
    }
  }, [refine, refined, onClose]);

  return (
    <Wrapper data-testid="active-filters">
      {activeFilters[COLOUR_FACET]?.items?.map((item) => {
        const splitLabel = item.label.split("||");
        const id = splitLabel[0];
        const label = splitLabel[1];
        const color = splitLabel[2];
        return (
          <Flex key={`color-filter-${id}`} variant="centered">
            <ColorBox
              id={id}
              hex={color}
              icon={color ? undefined : multicolorIconLink}
            />
            <StyledText>{label}</StyledText>
            <StyledCloseButton onClick={handleRefine(item.value)} />
          </Flex>
        );
      })}
      {minPrice !== undefined && maxPrice !== undefined && (
        <Flex variant="centered" mr={1} data-testid="price-filter">
          <StyledText>
            <Price price={minPrice} /> - <Price price={maxPrice} />
          </StyledText>
          <StyledCloseButton
            onClick={handleRefine(priceFacet?.value as RefinementValue)}
          />
        </Flex>
      )}
      {activeFilters[SIZE_FACET]?.items?.map((item) => (
        <RefineItem
          key={`size-filter-${item.label}`}
          item={item}
          refine={handleRefine(item.value)}
          data-testid="size-active-filter"
        />
      ))}
      {activeFilters[CATEGORIES_FACET]?.items?.map((item) => (
        <RefineItem
          key={`category-filter-${item.label}`}
          item={item}
          refine={handleRefine(item.value)}
        />
      ))}
      {promotionFacet && (
        <Flex variant="centered" mr={1}>
          <RefineItem
            item={promotionFacet}
            refine={handleRefine(promotionFacet.value)}
          />
        </Flex>
      )}
      {discountFacet && (
        <Flex variant="centered" mr={1}>
          <RefineItem
            item={discountFacet}
            refine={handleRefine(discountFacet.value)}
          />
        </Flex>
      )}
      {activeFilters[CONSCIOUS_FACET]?.items?.map((item) => (
        <RefineItem
          key={`conscious-filter-${item.label}`}
          item={item}
          refine={handleRefine(item.value)}
        />
      ))}
      {activeFilters[STYLE_FACET]?.items?.map((item) => (
        <RefineItem
          key={`style-filter-${item.label}`}
          item={item}
          refine={handleRefine(item.value)}
        />
      ))}
      {activeFilters[PATTERN_FACET]?.items?.map((item) => (
        <RefineItem
          key={`pattern-filter-${item.label}`}
          item={item}
          refine={handleRefine(item.value)}
        />
      ))}
      {activeFilters[AGES_FACET]?.items?.map((item) => (
        <RefineItem
          key={`age-filter-${item.label}`}
          item={item}
          refine={handleRefine(item.value)}
        />
      ))}
      {activeFilters[BRAND_FACET]?.items?.map((item) => (
        <RefineItem
          data-testid="brand-active-filter"
          key={`brand-filter-${item.label}`}
          item={item}
          refine={handleRefine(item.value)}
        />
      ))}
      {hasFilters && (
        <Button
          onClick={handleRefine(items, () => {
            onResetFilters();
          })}
          data-testid="reset-filters"
        >
          <Trans>Reset all filters</Trans>
        </Button>
      )}
    </Wrapper>
  );
};

export default connectCurrentRefinements(ActiveFilters);
